import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { openModal, closeModal } from '@repletoryreach/sdk-ui/modals';

/**
 * Whether a column must be there: `either` is the phone and email pair (4
 * October 2026), of which a file needs at least one.
 */
type Need = 'yes' | 'no' | 'either';

/** The columns `services/contacts` actually reads, in the order it reads them. */
const COLUMNS: { name: string; required: Need; note: string }[] = [
  { name: 'msisdn', required: 'either', note: 'csv.col.msisdn' },
  { name: 'name', required: 'no', note: 'csv.col.name' },
  { name: 'email', required: 'either', note: 'csv.col.emailKey' },
  { name: 'locale', required: 'no', note: 'csv.col.locale' },
  { name: 'tags', required: 'no', note: 'csv.col.tags' },
  { name: 'notes', required: 'no', note: 'csv.col.notes' },
  { name: 'email_opt_in', required: 'no', note: 'csv.col.email_opt_in' }
];

/** Read only while the platform offers SMS (4 October 2026); not shown otherwise. */
const SMS_COLUMN = { name: 'sms_opt_in', required: 'no' as Need, note: 'csv.col.sms_opt_in' };

const TEMPLATE = [
  'msisdn,name,email,locale,tags',
  '+27821234567,Thandi Mokoena,thandi@example.co.za,en,vip|newsletter',  // i18n-ignore: sample CSV row
  '+27829876543,Sipho Dlamini,,zu,newsletter',  // i18n-ignore: sample CSV row, not UI copy
  ',Lerato Khumalo,lerato@example.co.za,en,newsletter'  // i18n-ignore: sample CSV row, an email-only contact
].join('\n');

type Translate = (key: string, vars?: Record<string, string | number>) => string;

/**
 * What the importer expects, before the file picker opens.
 *
 * `contacts.md` §4 item 6: the button said "Import CSV" and opened a file
 * picker. The documented columns existed only in the service source, so the
 * first a user heard about the required shape was a row count that did not
 * match their file.
 */
export function openCsvFormatModal(t: Translate, onContinue: () => void, smsAvailable = false): void {
  const columns = smsAvailable ? [...COLUMNS, SMS_COLUMN] : COLUMNS;
  // `openModal` mints the id and returns it, so the buttons inside close by
  // capturing it rather than by naming a constant the store never knew about.
  let id = '';
  id = openModal({
    title: t('contacts.import.format.title'),
    children: (
      <Stack gap="md">
        <Text role="bodySmall">{t('contacts.import.format.intro')}</Text>

        <table className="rr-contacts__csv-cols">
          <thead>
            <tr>
              <th>{t('contacts.import.format.column')}</th>
              <th>{t('contacts.import.format.required')}</th>
              <th>{t('contacts.import.format.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <tr key={c.name}>
                <td><code>{c.name}</code></td>
                <td>
                  {c.required === 'either'
                    ? t('contacts.import.format.either')
                    : c.required === 'yes' ? t('common.yes') : t('common.no')}
                </td>
                <td>{t(`contacts.import.${c.note}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <Text role="bodySmall">{t('contacts.import.format.keyRule')}</Text>

        {/* Columns are matched by header now, so the order no longer matters
            and any other header is a custom field, by key or by label. */}
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('contacts.import.format.customFields')}
        </Text>
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('contacts.import.format.upsert')}
        </Text>

        <Group justify="space-between">
          <Button size="xs" variant="default" onClick={downloadTemplate}>
            {t('contacts.import.format.template')}
          </Button>
          <Group gap="xs">
            <Button size="xs" variant="default" onClick={() => closeModal(id)}>
              {t('action.cancel')}
            </Button>
            <Button size="xs" onClick={() => { closeModal(id); onContinue(); }}>
              {t('contacts.import.format.choose')}
            </Button>
          </Group>
        </Group>
      </Stack>
    )
  });
}

function downloadTemplate(): void {
  const blob = new Blob([TEMPLATE], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'contacts-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}
